import SideBarContainer from 'Containers/SideBarContainer';
import { useHistory, useParams } from 'react-router-dom';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import { Padding } from 'Components/Padding';
import useCourseEmployeesPage from './hooks/useCourseEmployeesPage';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import ReportsDataGrid from 'Pages/reports/components/ReportsDataGrid/ReportsDataGrid';
import { ReportsButtonGroup } from 'Pages/reports/components/ReportsButtonGroup/ReportsButtonGroup';
import TotalCourseDetails from './components/TotalCourseDetails/TotalCourseDetails';
import ProgressIndicator from 'Components/ProgressIndicator';
import CourseStatusChip from 'Components/CourseStatusChip';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import CourseEmployeesFilters from './components/CourseEmployeesFilters/CourseEmployeesFilters';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';
import type { GridColDef } from '@mui/x-data-grid';

interface Params {
  courseId: string;
}

interface Row {
  id: string;
  payrollNum: number | string;
  fullName: string;
  departmentName: string;
  jobTitleName: string;
  jobTitleTypeName: string;
  gender: string;
  age: number | string;
  entryDate: string;
  completedEvaluations: number;
  totalEvaluations: number;
  userCourseStatusId: string;
  userCourseStatusName: string;
  certificationId: string | null;
}

export default function Page() {
  const { courseId } = useParams<Params>();
  const history = useHistory();
  const {
    t2,
    course,
    totals,
    courseEmployees,
    pagination,
    loading,
    error,
    handlePageChange,
    handleRowsPerPageChange,
    onDownloadCertificate,
    onExportExcel,
    onSearch,
  } = useCourseEmployeesPage(courseId);

  const buttons = [
    { icon: <EmployeesReportIcon />, tooltip: t2('employees_report_tooltip'), route: '/reporte/empleados' },
    { icon: <CoursesReportIcon />, tooltip: t2('courses_report_tooltip'), route: '/reporte/cursos' },
  ];

  const columns: GridColDef[] = [
    {
      field: 'payrollNum',
      headerName: t2('payroll_number'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'fullName', headerName: t2('full_name'), flex: 1.5 },
    { field: 'departmentName', headerName: t2('department_name'), flex: 1 },
    { field: 'jobTitleName', headerName: t2('job_title_name'), flex: 1 },
    { field: 'jobTitleTypeName', headerName: t2('job_title_type_name'), flex: 1 },
    { field: 'gender', headerName: t2('gender'), flex: 0.8 },
    { field: 'age', headerName: t2('age'), flex: 0.6 },
    { field: 'entryDate', headerName: t2('entry_date'), flex: 1 },
    {
      field: 'completedEvaluations',
      headerName: t2('completed_evaluations'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <ProgressIndicator
          completed={params.row.completedEvaluations}
          total={params.row.totalEvaluations}
          emptyMessage={t2('no_evaluations')}
        />
      ),
    },
    {
      field: 'userCourseStatusName',
      headerName: t2('user_course_status_name'),
      flex: 1.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <CourseStatusChip
          status={params.row.userCourseStatusName}
          statusId={params.row.userCourseStatusId}
        />
      ),
    },
    {
      field: 'downloadCertificate',
      headerName: t2('download_certificate'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: params => (
        <CertificateDownload
          certificationId={params.row.certificationId}
          onDownload={() => onDownloadCertificate(courseId, params.row.id, params.row.certificationId ?? '')}
        />
      ),
    },
  ];

  const rows: Row[] = courseEmployees.map(employee => ({
    id: employee.id,
    payrollNum: employee.payrollNum,
    fullName: employee.fullName,
    departmentName: employee.departmentName,
    jobTitleName: employee.jobTitleName,
    jobTitleTypeName: employee.jobTitleTypeName,
    gender: employee.gender,
    age: employee.age,
    entryDate: employee.entryDate,
    completedEvaluations: employee.completedEvaluations,
    totalEvaluations: employee.totalEvaluations,
    userCourseStatusId: employee.userCourseStatusId,
    userCourseStatusName: employee.userCourseStatusName,
    certificationId: employee.certificationId,
  }));

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('courses_report'), path: '/reporte/cursos' },
            { label: t2('course_detail'), path: `/reporte/cursos/${courseId}` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${course?.name}"`} />}
        buttons={<ReportsButtonGroup buttons={buttons} />}
        onExport={onExportExcel}
      />
      <Padding padding="1rem">
        <TotalCourseDetails totals={totals} />
        <CourseEmployeesFilters onSearch={onSearch} />
        <Padding padding="0.5rem 0" />
        <ReportsDataGrid<Row>
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={row => history.push(`/reporte/empleados/${row.id}`)}
        />
      </Padding>
    </SideBarContainer>
  );
}
